import { useCallback, useEffect, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import { parsePipelineStages } from '../lib/crm/opportunityAutomationContext';

export interface OpenLeadsByStageRow {
  name: string;
  count: number;
}

/**
 * Counts currently-open opportunities grouped by their pipeline stage name
 * (merged across pipelines), for the control-centre stage donut. Sorted by
 * count descending.
 */
export function useOpenLeadsByStage() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [stages, setStages] = useState<OpenLeadsByStageRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setStages([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    try {
      const [{ data: opps, error: oppError }, { data: pipelines, error: pipelineError }] =
        await Promise.all([
          supabase
            .from('pipeline_opportunities')
            .select('stage_id, pipeline_id')
            .eq('business_id', businessId)
            .eq('status', 'Open'),
          supabase.from('pipelines').select('id, stages').eq('business_id', businessId),
        ]);

      if (oppError) throw oppError;
      if (pipelineError) throw pipelineError;

      // pipeline_id -> (stage_id -> stage name)
      const stageNamesByPipeline = new Map<number, Map<number, string>>();
      for (const pipeline of pipelines ?? []) {
        const map = new Map<number, string>();
        for (const stage of parsePipelineStages(pipeline.stages)) {
          map.set(Number(stage.id), stage.name);
        }
        stageNamesByPipeline.set(pipeline.id, map);
      }

      const countByName = new Map<string, number>();
      for (const opp of opps ?? []) {
        const name =
          stageNamesByPipeline.get(opp.pipeline_id)?.get(Number(opp.stage_id)) || 'Unstaged';
        countByName.set(name, (countByName.get(name) ?? 0) + 1);
      }

      const rows = Array.from(countByName.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      setStages(rows);
      setTotal(rows.reduce((sum, row) => sum + row.count, 0));
    } catch (err) {
      console.error('Error loading open leads by stage:', err);
      setStages([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId]);

  useEffect(() => {
    setLoading(true);
    void load();
    const interval = setInterval(() => void load(), 60_000);
    return () => clearInterval(interval);
  }, [load]);

  return { stages, total, loading, refresh: load };
}
